import { useEffect, useRef, useState } from 'react';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';

import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Box,
  Divider,
  VStack,
  FormControl,
  FormErrorMessage,
  FormHelperText,
} from '@chakra-ui/react';

import { updateNarrativeSubQuest } from 'store/modules/subquest/subquest-action';
import { subquestActions } from 'store/modules/subquest/subquest-slice';
import { ReducerStatus } from 'utils/constants';
import SubQuestForm from 'components/subQuest/SubQuestForm';
import ResultModal from 'components/common/modals/ResultModal';
import { arrayOfString } from 'utils/jsonParser';

import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import imageCompressor from 'quill-image-compress';

import {
  useQuillConfig,
  calculateContentSize,
} from 'utils/react-quill/quillConfig';
import RichTextSizeIndicator from 'utils/react-quill/RichTextSizeIndicator';

ReactQuill.Quill.register('modules/imageCompress', imageCompressor);

function UpdateNarrativeMd() {
  const dispatch = useDispatch();
  const [triggerSubQuestForm, setTriggerSubQuestForm] = useState(0);
  const [isSubQuestFormValid, setIsSubQuestFormValid] = useState(false);
  const [isFormSubmit, setIsFormSubmit] = useState(false);
  const [contentSize, setContentSize] = useState(0);
  const [subQuestFormInputs, setSubQuestFormInputs] = useState({
    name: '',
    objective: '',
    code: '',
    isPublished: '',
    tags: [],
    groups: [],
  });

  const [formInputs, setFormInputs] = useState({
    content: '',
  });

  const subQuest = useSelector((state) => state.subquest.subQuest);
  const questId = useSelector((state) => state.subquest.questId);
  const updateNarrative = useSelector(
    (state) => state.subquest.updateNarrative
  );

  const formikForm = useRef();
  const quillRef = useRef(null);

  const { modules, formats, style } = useQuillConfig();

  const formValidation = Yup.object({
    content: Yup.string()
      .test('not-empty', 'Content is required', (value) => {
        if (!value) return false;
        const textContent = value.replace(/<[^>]*>/g, '').trim();
        return textContent.length > 0;
      })
      .test('file-size', 'Content size exceeds 5MB limit', (value) => {
        if (!value) return true;
        const calculatedSize = calculateContentSize(value);
        return calculatedSize <= 5;
      })
      .required('Content is required'),
  });

  useEffect(() => {
    if (subQuest && updateNarrative.modal) {
      const { name, objective, code, isPublished, tags, ownerGroups } =
        subQuest;
      const { content } = subQuest.subQuestNarrative;

      setIsSubQuestFormValid(false);
      setSubQuestFormInputs({
        name,
        objective,
        code,
        isPublished: isPublished.toString(),
        tags: arrayOfString(tags),
        groups: arrayOfString(ownerGroups),
      });

      setFormInputs({
        content,
      });

      setContentSize(calculateContentSize(content));
    }
  }, [subQuest, updateNarrative.modal]);

  const handleTriggerSubmit = () => {
    formikForm.current?.setFieldTouched('content', true, false);
    formikForm.current?.validateField('content');

    setTriggerSubQuestForm((trigger) => trigger + 1);
    setIsFormSubmit(true);
  };

  const handleSubmitForm = (values) => {
    if (isSubQuestFormValid === false) return;

    setFormInputs({ ...values });

    const narrativeData = {
      Name: subQuestFormInputs.name,
      Objective: subQuestFormInputs.objective,
      Code: subQuestFormInputs.code,
      IsPublished:
        subQuestFormInputs.isPublished === 'true' ||
        subQuestFormInputs.isPublished === true,
      Tags: subQuestFormInputs.tags || [],
      OwnerGroups: subQuestFormInputs.groups || [],
      Content: values.content,
    };

    dispatch(
      updateNarrativeSubQuest({
        questId,
        subQuestId: subQuest.id,
        formData: narrativeData,
      })
    );
  };

  useEffect(() => {
    if (isFormSubmit && isSubQuestFormValid) {
      formikForm?.current?.submitForm();
      setIsFormSubmit(false);
    }
  }, [isFormSubmit, isSubQuestFormValid]);

  const handleToggleUpdateModal = (isOpen) => {
    dispatch(subquestActions.toggleNarrativeUpdateModal(isOpen));
    if (!isOpen) {
      dispatch(subquestActions.resetSubQuestState());
    }
  };

  const handleResult = () => {
    if (updateNarrative.status === ReducerStatus.SUCCEEDED) {
      setFormInputs({
        content: '',
      });

      setSubQuestFormInputs({
        name: '',
        objective: '',
        code: '',
        isPublished: '',
        tags: [],
        groups: [],
      });

      setTriggerSubQuestForm(0);
      setIsSubQuestFormValid(false);
      handleToggleUpdateModal(false);
    }
    dispatch(subquestActions.resetUpdateNarrativeStatus());
  };

  return (
    <Box>
      {updateNarrative.status === ReducerStatus.SUCCEEDED ||
      updateNarrative.status === ReducerStatus.FAILED ? (
        <ResultModal
          isOpen={updateNarrative.modal}
          onSubmit={handleResult}
          isError={updateNarrative.status === ReducerStatus.FAILED}
          title={
            updateNarrative.status === ReducerStatus.SUCCEEDED
              ? 'Narrative Sub-Quest Updated'
              : null
          }
          message={
            updateNarrative.status === ReducerStatus.SUCCEEDED
              ? 'The narrative sub-quest has been updated successfully.'
              : updateNarrative.error?.data?.title || 'Server Error'
          }
          errorObject={updateNarrative.error?.data?.errors}
        />
      ) : (
        <Modal
          size="2xl"
          isOpen={updateNarrative.modal}
          closeOnOverlayClick={updateNarrative.status !== ReducerStatus.LOADING}
          onClose={() => handleToggleUpdateModal(false)}
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Update Narrative Sub-Quest</ModalHeader>
            <ModalCloseButton
              disabled={updateNarrative.status === ReducerStatus.LOADING}
            />
            <ModalBody my={3}>
              <SubQuestForm
                subQuestFormInputs={subQuestFormInputs}
                triggerSubQuestForm={triggerSubQuestForm}
                setSubQuestFormInputs={setSubQuestFormInputs}
                setIsSubQuestFormValid={setIsSubQuestFormValid}
              />
            </ModalBody>
            <Formik
              enableReinitialize
              innerRef={formikForm}
              initialValues={formInputs}
              onSubmit={handleSubmitForm}
              validationSchema={formValidation}
            >
              {({ errors, touched }) => (
                <Box>
                  <Form>
                    <ModalBody>
                      <Divider mb={4} />
                      <VStack spacing={4} align="stretch">
                        <FormControl
                          isInvalid={errors.content && touched.content}
                        >
                          <Field name="content">
                            {({ field, form }) => (
                              <Box position="relative" sx={style}>
                                <ReactQuill
                                  ref={quillRef}
                                  value={field.value}
                                  onChange={(content) => {
                                    const newContentSize =
                                      calculateContentSize(content);
                                    setContentSize(newContentSize);
                                    form.setFieldValue(field.name, content);
                                  }}
                                  onBlur={() => {
                                    form.setFieldTouched(field.name, true);
                                  }}
                                  placeholder="Enter narrative content"
                                  style={{
                                    border:
                                      form.errors.content &&
                                      form.touched.content
                                        ? '2px solid #e53e3e'
                                        : '1px solid #e2e8f0',
                                    borderRadius: '6px',
                                  }}
                                  theme="snow"
                                  modules={modules}
                                  formats={formats}
                                />
                                <RichTextSizeIndicator size={contentSize} />
                              </Box>
                            )}
                          </Field>
                          <FormErrorMessage>{errors.content}</FormErrorMessage>
                          <FormHelperText>
                            Update the narrative content using the rich text
                            editor
                          </FormHelperText>
                        </FormControl>
                      </VStack>
                    </ModalBody>

                    <ModalFooter>
                      <Button
                        variant="outline"
                        colorScheme="blue"
                        mr={3}
                        onClick={() => handleToggleUpdateModal(false)}
                        isLoading={
                          updateNarrative.status === ReducerStatus.LOADING
                        }
                      >
                        Close
                      </Button>
                      <Button
                        isLoading={
                          updateNarrative.status === ReducerStatus.LOADING
                        }
                        onClick={handleTriggerSubmit}
                        colorScheme="red"
                        px={10}
                      >
                        Submit
                      </Button>
                    </ModalFooter>
                  </Form>
                </Box>
              )}
            </Formik>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
}

export default UpdateNarrativeMd;
